import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";

const TERMS_LAST_REVIEWED = "2026-07-05";

function Section({ eyebrow, title, children }) {
  return (
    <section className="paper-card px-6 py-5 sm:px-8 sm:py-6" data-testid={`terms-section-${eyebrow.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
      <p className="eyebrow mb-2">{eyebrow}</p>
      <h2 className="font-display text-xl font-medium leading-snug text-foreground">{title}</h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">
        {children}
      </div>
    </section>
  );
}

export default function TermsPage() {
  return (
    <PageShell>
      <div className="container py-10 lg:py-14" data-testid="terms-page">
        <div className="flex flex-col gap-3">
          <p className="eyebrow">§ Terms of use</p>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h1 className="font-display text-3xl font-medium leading-tight text-foreground sm:text-4xl">
              Terms of use
            </h1>
            <Button
              asChild
              variant="ghost"
              className="rounded-sm px-3 text-[13px] font-medium text-foreground hover:bg-secondary"
            >
              <Link to="/" data-testid="terms-back-home">
                <ArrowLeft className="h-4 w-4" />
                Back to home
              </Link>
            </Button>
          </div>
          <p className="max-w-3xl text-base leading-relaxed text-muted-foreground">
            NDIS 0138 Readiness is a consultancy readiness tool. These terms describe what the tool
            does, what it does not do, and how a provider or consultant should responsibly rely on
            its output.
          </p>
          <p className="max-w-3xl text-xs text-muted-foreground">
            Last reviewed: <span className="font-mono-label">{TERMS_LAST_REVIEWED}</span>
          </p>
        </div>

        <div className="mt-8 space-y-5">
          <Section eyebrow="§ 01 · Purpose" title="Purpose of the tool">
            <p>
              NDIS 0138 Readiness is a preliminary readiness diagnostic for providers who deliver, or
              plan to deliver, Supported Independent Living (SIL) supports under the NDIS. It
              helps a qualified consultant surface evidence gaps, safeguarding signals, and
              operational risks against the R.E.A.D.Y. framework, and produces a preliminary
              snapshot report that is intended to guide consultation.
            </p>
          </Section>

          <Section eyebrow="§ 02 · Boundary" title="Consultancy-use boundary">
            <p>
              This tool is designed for use by, or under the direction of, a qualified NDIS
              readiness consultant. Its outputs are preliminary and are intended to inform a
              guided review conversation. They are not a substitute for the professional judgement
              of a qualified consultant, an Approved Quality Auditor, or a legal adviser.
            </p>
          </Section>

          <Section
            eyebrow="§ 03 · Not advice"
            title="Not legal, regulatory, financial, medical, or registration advice"
          >
            <p>
              Nothing produced by this tool constitutes legal advice, regulatory advice, financial
              advice, medical advice, tax advice, or registration advice. Nothing produced by this
              tool constitutes advice on how any specific participant, worker, or matter should be
              handled. The tool does not create a professional relationship between the provider
              or consultant and the tool's authors.
            </p>
          </Section>

          <Section
            eyebrow="§ 04 · No outcome guarantee"
            title="No guarantee of registration, audit, compliance, funding, payment, or provider outcome"
          >
            <p>
              The tool does not guarantee any NDIS registration outcome, Approved Quality Auditor
              outcome, quality-and-safeguards compliance finding, funding or payment outcome, or
              any other provider outcome. The tool is not endorsed by, affiliated with, or
              operated on behalf of the NDIS Quality and Safeguards Commission, the National
              Disability Insurance Agency, or any government body.
            </p>
          </Section>

          <Section
            eyebrow="§ 05 · Source verification"
            title="Source-verification basis and date-stamped references"
          >
            <p>
              Each regulatory reference used by the tool is recorded in an internal source
              register with an owner, a URL, an authority level, and a confirmation date. Where
              the report's Source Verification panel displays a status of <span className="font-mono-label">VERIFIED</span>,
              the applicable finalised official sources for the current SIL assessment pathway
              have been confirmed against their current published version as of the confirmation
              date shown. Where the panel displays a status of <span className="font-mono-label">REQUIRES_SOURCE_VERIFICATION</span>,
              <span className="font-mono-label"> INTERNAL_ONLY</span>, or <span className="font-mono-label">DO_NOT_PUBLISH</span>,
              the report should not be treated as source-verified and the referenced sources
              should be re-opened before the report is relied on externally. Confirmation of the
              broader NDIS Practice Standards review is not implied — that reform remains in
              progress under a separate advisory register entry.
            </p>
          </Section>

          <Section
            eyebrow="§ 06 · User responsibility"
            title="User responsibility for checking current official requirements"
          >
            <p>
              Regulatory guidance changes. Users of this tool are responsible for checking the
              current published versions of any NDIS Quality and Safeguards Commission or
              National Disability Insurance Agency guidance referenced in a report before that
              report is relied on or shared with a provider. The tool's confirmation dates
              indicate when the consultancy last checked a source — not when the underlying
              publication was last changed.
            </p>
          </Section>

          <Section eyebrow="§ 07 · Data and privacy" title="Data and privacy summary">
            <p>
              Assessment progress is held in your browser (<span className="font-mono-label">localStorage</span>) so
              you can resume later. Nothing is sent to a server unless you click Save to Server.
              Server records are de-identified provider snapshots only and are retained until the
              consultancy manually removes them. No participant data, contact details, or
              free-text notes are transmitted. See the app footer for the current data-and-privacy
              statement.
            </p>
          </Section>

          <Section
            eyebrow="§ 08 · Limitation of liability"
            title="Limitation of liability (placeholder)"
          >
            <p>
              To the maximum extent permitted by law, <span className="font-mono-label">[CONSULTANCY LEGAL NAME]</span>{" "}
              is not liable for any loss, damage, or claim arising from reliance on outputs
              produced by this tool, whether or not that reliance was foreseeable. This placeholder
              wording is provided pending owner and legal review; final liability wording, warranty
              disclaimers, and jurisdictional carve-outs must be inserted by{" "}
              <span className="font-mono-label">[CONSULTANCY LEGAL NAME]</span> before this tool is
              used with paying clients.
            </p>
          </Section>

          <Section eyebrow="§ 09 · Contact and owner" title="Contact / owner">
            <p>
              For questions about these terms, source-register accuracy, or a specific readiness
              output, contact{" "}
              <span className="text-foreground">
                JOHN MARTIN KENNEALLY ABN 82 097 590 964 trading as Spiral Beyond Compliance
              </span>{" "}
              at{" "}
              <a
                href="mailto:hello@spiralbeyondcompliance.com.au"
                data-testid="terms-contact-email"
                className="font-mono-label underline decoration-border underline-offset-4 hover:text-foreground"
              >
                hello@spiralbeyondcompliance.com.au
              </a>
              . These terms are governed by the law of{" "}
              <span className="text-foreground">Queensland, Australia</span> pending final
              owner review.
            </p>
          </Section>

          <Section
            eyebrow="§ 10 · Version"
            title={`Terms version — last reviewed ${TERMS_LAST_REVIEWED}`}
          >
            <p>
              These terms are versioned by review date. Substantive changes will be recorded
              against a new review date. This is the initial owner-review draft; adjust wording
              in <span className="font-mono-label">/app/frontend/src/pages/TermsPage.jsx</span>{" "}
              when the owner completes their review.
            </p>
          </Section>
        </div>
      </div>
    </PageShell>
  );
}
